import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  FileText, Send, CheckCircle2, XCircle, Eye, Edit2, Plus, Loader2,
  Copy, ExternalLink, DollarSign, Trash2, Link2, Download, GripVertical,
  Percent, Save, FileDown, FileSpreadsheet, FileType, ArrowUp, ArrowDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: Edit2 },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  revised: { label: 'Revised', color: 'bg-yellow-100 text-yellow-700', icon: Edit2 },
};

function InlineEditCell({ value, field, type = "text", onSave, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const save = () => { onSave(field, type === "number" ? parseFloat(val) || 0 : val); setEditing(false); };
  if (editing) {
    return (
      <Input
        type={type}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
        className={`h-6 text-[10px] px-1 w-full ${className}`}
      />
    );
  }
  return (
    <span
      className={`cursor-pointer hover:bg-[#72A4BF]/10 px-1 py-0.5 rounded transition-colors ${className}`}
      onClick={() => { setVal(value); setEditing(true); }}
      title="Click to edit"
    >
      {type === "number" && field.includes("cost") || field === "price" ? `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : value || '-'}
    </span>
  );
}

export default function QuoteWorkflow({ projectId, onQuoteChange }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [editingQuote, setEditingQuote] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [pricingDialog, setPricingDialog] = useState(null); // { componentName, field, value, qiId }
  const [newQuote, setNewQuote] = useState({
    quote_name: '', customer_name: '', customer_email: '', terms_conditions: '', valid_until: ''
  });
  const [newItem, setNewItem] = useState({
    component_name: '', quantity: 0, unit: 'EA', cost: 0, price: 0, division: '', assembly: '', notes: ''
  });

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/quotes?project_id=${projectId}`, { credentials: 'include' });
      if (res.ok) setQuotes(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const createQuote = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newQuote, project_id: projectId })
      });
      if (res.ok) {
        const q = await res.json();
        setShowCreate(false);
        setNewQuote({ quote_name: '', customer_name: '', customer_email: '', terms_conditions: '', valid_until: '' });
        setEditingQuote(q);
        fetchQuotes();
        onQuoteChange?.();
      }
    } catch (e) {}
    finally { setCreating(false); }
  };

  const sendQuote = async (quoteId) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes/${quoteId}/send`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setShareLink(data.share_link || '');
        setShowSend(true);
        fetchQuotes();
        onQuoteChange?.();
      }
    } catch (e) {}
    finally { setSending(false); }
  };

  const changeQuoteStatus = async (quoteId, newStatus) => {
    try {
      await fetch(`${API}/quotes/${quoteId}/status/${newStatus}`, { method: 'POST', credentials: 'include' });
      fetchQuotes();
      onQuoteChange?.();
    } catch (e) {}
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await fetch(`${API}/quotes/${quoteId}`, { method: 'DELETE', credentials: 'include' });
      if (editingQuote?.quote_id === quoteId) setEditingQuote(null);
      fetchQuotes();
    } catch (e) {}
  };

  const addItem = async () => {
    if (!editingQuote || !newItem.component_name) return;
    try {
      const res = await fetch(`${API}/quotes/${editingQuote.quote_id}/items`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(newItem)
      });
      if (res.ok) {
        const q = await res.json();
        setEditingQuote(q);
        setShowAddItem(false);
        setNewItem({ component_name: '', quantity: 0, unit: 'EA', cost: 0, price: 0, division: '', assembly: '', notes: '' });
        fetchQuotes();
      }
    } catch (e) {}
  };

  const removeItem = async (qiId) => {
    if (!editingQuote) return;
    try {
      const res = await fetch(`${API}/quotes/${editingQuote.quote_id}/items/${qiId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { const q = await res.json(); setEditingQuote(q); fetchQuotes(); }
    } catch (e) {}
  };

  const updateItem = async (qiId, field, value) => {
    if (!editingQuote) return;
    // If changing price or cost and multiple quotes exist, offer to apply to all
    if ((field === 'price' || field === 'cost') && quotes.length > 1) {
      const item = (editingQuote.items || []).find(i => i.qi_id === qiId);
      if (item) {
        setPricingDialog({ componentName: item.component_name, field, value, qiId });
        return;
      }
    }
    await doUpdateItem(qiId, field, value);
  };

  const doUpdateItem = async (qiId, field, value) => {
    if (!editingQuote) return;
    try {
      const res = await fetch(`${API}/quotes/${editingQuote.quote_id}/items/${qiId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });
      if (res.ok) { const q = await res.json(); setEditingQuote(q); fetchQuotes(); }
    } catch (e) {}
  };

  const applyPricingToAll = async () => {
    if (!pricingDialog || !editingQuote) return;
    const { componentName, field, value, qiId } = pricingDialog;
    // First update this quote's item
    await doUpdateItem(qiId, field, value);
    // Then bulk-apply to all quotes
    try {
      const body = { component_name: componentName, apply_to_all: true };
      if (field === 'price') body.new_price = value;
      if (field === 'cost') body.new_cost = value;
      await fetch(`${API}/projects/${projectId}/adjust-pricing`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      fetchQuotes();
    } catch (e) {}
    setPricingDialog(null);
  };

  const applyPricingToOne = async () => {
    if (!pricingDialog) return;
    await doUpdateItem(pricingDialog.qiId, pricingDialog.field, pricingDialog.value);
    setPricingDialog(null);
  };

  const moveItem = async (idx, dir) => {
    if (!editingQuote) return;
    const items = editingQuote.items || [];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const order = items.map(i => i.qi_id);
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    try {
      const res = await fetch(`${API}/quotes/${editingQuote.quote_id}/reorder`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ order })
      });
      if (res.ok) { const q = await res.json(); setEditingQuote(q); fetchQuotes(); }
    } catch (e) {}
  };

  const saveQuoteSettings = async (updates) => {
    if (!editingQuote) return;
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/quotes/${editingQuote.quote_id}/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (res.ok) { const q = await res.json(); setEditingQuote(q); fetchQuotes(); }
    } catch (e) {}
    finally { setSavingSettings(false); }
  };

  const exportQuote = async (fmt, style = "professional") => {
    if (!editingQuote) return;
    setExporting(fmt);
    try {
      // v5 template applies to PDF and Excel only; DOCX stays on legacy.
      const useV5 = fmt === 'pdf' || fmt === 'excel';
      const templateParam = useV5 ? '&template=v5' : '';
      const url = `${API}/quotes/${editingQuote.quote_id}/export/${fmt}?style=${style}${templateParam}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        const ext = fmt === 'excel' ? 'xlsx' : fmt;
        a.download = `${editingQuote.quote_name || 'quote'}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      } else {
        toast.error('Export failed. Please try again.');
      }
    } catch (e) {
      toast.error('Export failed. Please try again.');
    }
    finally { setExporting(null); }
  };

  const fmtC = (v) => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';

  // Calculate grand total with tax/markup/discount
  const calcGrandTotal = (q) => {
    if (!q) return 0;
    const sub = q.total_price || 0;
    const tax = sub * ((q.tax_rate || 0) / 100);
    const markup = sub * ((q.global_markup || 0) / 100);
    const discount = sub * ((q.discount_percent || 0) / 100);
    return sub + tax + markup - discount;
  };

  // Editing mode: show full worksheet
  if (editingQuote) {
    const items = editingQuote.items || [];
    const grand = calcGrandTotal(editingQuote);
    return (
      <div className="space-y-4" data-testid="quote-worksheet">
        {/* Worksheet header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setEditingQuote(null)} className="h-7 text-xs" data-testid="back-to-quotes">
              &larr; Back
            </Button>
            <h3 className="text-sm font-bold text-[#0D1830]">{editingQuote.quote_name}</h3>
            <Badge className={`${(QUOTE_STATUS_CONFIG[editingQuote.status] || QUOTE_STATUS_CONFIG.draft).color} border-0 text-[9px] px-1.5 py-0`}>
              {(QUOTE_STATUS_CONFIG[editingQuote.status] || QUOTE_STATUS_CONFIG.draft).label}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {/* Export buttons */}
            <div className="flex items-center border border-[#E2E8ED] rounded-full overflow-hidden">
              <button onClick={() => exportQuote('pdf')} disabled={!!exporting}
                className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1 border-r border-[#E2E8ED]" data-testid="export-pdf-btn">
                {exporting === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileType className="w-3 h-3 text-red-500" />}PDF
              </button>
              <button onClick={() => exportQuote('docx')} disabled={!!exporting}
                className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1 border-r border-[#E2E8ED]" data-testid="export-docx-btn">
                {exporting === 'docx' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 text-blue-500" />}Word
              </button>
              <button onClick={() => exportQuote('excel')} disabled={!!exporting}
                className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1" data-testid="export-excel-btn">
                {exporting === 'excel' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 text-emerald-600" />}Excel
              </button>
            </div>
            <Button onClick={() => exportQuote('pdf', 'detailed')} variant="outline" size="sm" className="h-7 text-[10px] rounded-full" data-testid="export-detailed-btn">
              <Download className="w-3 h-3 mr-1" />Detailed PDF
            </Button>
          </div>
        </div>

        {/* Quote settings bar */}
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[#72A4BF]">Tax %</span>
                <Input type="number" value={editingQuote.tax_rate || ''} placeholder="0"
                  onChange={(e) => saveQuoteSettings({ tax_rate: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs" data-testid="tax-rate-input" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[#72A4BF]">Markup %</span>
                <Input type="number" value={editingQuote.global_markup || ''} placeholder="0"
                  onChange={(e) => saveQuoteSettings({ global_markup: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs" data-testid="markup-input" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[#72A4BF]">Discount %</span>
                <Input type="number" value={editingQuote.discount_percent || ''} placeholder="0"
                  onChange={(e) => saveQuoteSettings({ discount_percent: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs" data-testid="discount-input" />
              </div>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-xs text-[#72A4BF]">Subtotal: <span className="font-medium text-[#0D1830]">{fmtC(editingQuote.total_price)}</span></span>
                <span className="text-sm font-bold gold-text" data-testid="grand-total">Total: {fmtC(grand)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items table */}
        <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-[#F7FAFB] border-b border-[#E2E8ED]">
            <span className="text-xs font-semibold text-[#0D1830]">{items.length} Line Items</span>
            <Button size="sm" onClick={() => setShowAddItem(true)} className="bg-[#0D1830] text-white rounded-full h-7 text-[10px] px-3 hover:opacity-90" data-testid="add-item-btn">
              <Plus className="w-3 h-3 mr-1" />Add Item
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                <TableHead className="text-[10px] w-8"></TableHead>
                <TableHead className="text-[10px]">Component</TableHead>
                <TableHead className="text-[10px]">Division</TableHead>
                <TableHead className="text-[10px] text-right">Qty</TableHead>
                <TableHead className="text-[10px]">Unit</TableHead>
                <TableHead className="text-[10px] text-right">Cost</TableHead>
                <TableHead className="text-[10px] text-right">Price</TableHead>
                <TableHead className="text-[10px] text-right">Margin</TableHead>
                <TableHead className="text-[10px] text-right">Total</TableHead>
                <TableHead className="text-[10px] w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={item.qi_id} className="border-[#F0F4F7] group" data-testid={`quote-item-${item.qi_id}`}>
                  <TableCell className="text-[10px] text-[#72A4BF]/60">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="hover:text-[#0D1830] disabled:opacity-20"><ArrowUp className="w-2.5 h-2.5" /></button>
                      <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="hover:text-[#0D1830] disabled:opacity-20"><ArrowDown className="w-2.5 h-2.5" /></button>
                    </div>
                  </TableCell>
                  <TableCell className="text-[10px] font-medium max-w-[160px]">
                    <InlineEditCell value={item.component_name} field="component_name" onSave={(f, v) => updateItem(item.qi_id, f, v)} />
                  </TableCell>
                  <TableCell className="text-[10px] text-[#72A4BF] max-w-[100px]">
                    <InlineEditCell value={item.division} field="division" onSave={(f, v) => updateItem(item.qi_id, f, v)} />
                  </TableCell>
                  <TableCell className="text-[10px] text-right">
                    <InlineEditCell value={item.quantity} field="quantity" type="number" onSave={(f, v) => updateItem(item.qi_id, f, v)} />
                  </TableCell>
                  <TableCell className="text-[10px]">
                    <InlineEditCell value={item.unit} field="unit" onSave={(f, v) => updateItem(item.qi_id, f, v)} />
                  </TableCell>
                  <TableCell className="text-[10px] text-right">
                    <InlineEditCell value={item.cost} field="cost" type="number" onSave={(f, v) => updateItem(item.qi_id, f, v)} className="text-right" />
                  </TableCell>
                  <TableCell className="text-[10px] text-right">
                    <InlineEditCell value={item.price} field="price" type="number" onSave={(f, v) => updateItem(item.qi_id, f, v)} className="text-right" />
                  </TableCell>
                  <TableCell className="text-[10px] text-right">
                    <span className={`${(item.margin || 0) > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {(item.margin || 0).toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-[10px] text-right font-medium">{fmtC(item.total_price)}</TableCell>
                  <TableCell className="text-[10px]">
                    <button onClick={() => removeItem(item.qi_id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity" data-testid={`remove-item-${item.qi_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={10} className="py-8 text-center text-xs text-[#72A4BF]">No items yet. Click "Add Item" to start building your quote.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Totals summary */}
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4">
            <div className="flex justify-end">
              <div className="space-y-1.5 text-right w-64">
                <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Subtotal ({items.length} items)</span><span className="font-medium">{fmtC(editingQuote.total_price)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Cost Total</span><span>{fmtC(editingQuote.total_cost)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Margin</span><span className={editingQuote.total_margin > 0 ? 'text-emerald-600' : 'text-red-500'}>{(editingQuote.total_margin || 0).toFixed(1)}%</span></div>
                {editingQuote.global_markup > 0 && <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Markup ({editingQuote.global_markup}%)</span><span>+{fmtC(editingQuote.total_price * editingQuote.global_markup / 100)}</span></div>}
                {editingQuote.discount_percent > 0 && <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Discount ({editingQuote.discount_percent}%)</span><span className="text-red-500">-{fmtC(editingQuote.total_price * editingQuote.discount_percent / 100)}</span></div>}
                {editingQuote.tax_rate > 0 && <div className="flex justify-between text-xs"><span className="text-[#72A4BF]">Tax ({editingQuote.tax_rate}%)</span><span>+{fmtC(editingQuote.total_price * editingQuote.tax_rate / 100)}</span></div>}
                <div className="flex justify-between pt-2 border-t border-[#1D4053]">
                  <span className="text-sm font-bold">Grand Total</span>
                  <span className="text-lg font-bold gold-text">{fmtC(grand)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add item dialog */}
        <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Line Item</DialogTitle>
              <DialogDescription>Add a new component to this quote</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-[#0D1830] mb-1 block">Component Name *</label>
                <Input value={newItem.component_name} onChange={(e) => setNewItem(p => ({...p, component_name: e.target.value}))} placeholder="e.g., 2x4 Wood Stud" data-testid="new-item-name" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Quantity</label><Input type="number" value={newItem.quantity} onChange={(e) => setNewItem(p => ({...p, quantity: parseFloat(e.target.value) || 0}))} data-testid="new-item-qty" /></div>
                <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Unit</label><Input value={newItem.unit} onChange={(e) => setNewItem(p => ({...p, unit: e.target.value}))} placeholder="EA" /></div>
                <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Division</label><Input value={newItem.division} onChange={(e) => setNewItem(p => ({...p, division: e.target.value}))} placeholder="06 - Wood" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Unit Cost ($)</label><Input type="number" value={newItem.cost} onChange={(e) => setNewItem(p => ({...p, cost: parseFloat(e.target.value) || 0}))} data-testid="new-item-cost" /></div>
                <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Unit Price ($)</label><Input type="number" value={newItem.price} onChange={(e) => setNewItem(p => ({...p, price: parseFloat(e.target.value) || 0}))} data-testid="new-item-price" /></div>
              </div>
              <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Notes</label><Input value={newItem.notes} onChange={(e) => setNewItem(p => ({...p, notes: e.target.value}))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddItem(false)} className="rounded-full">Cancel</Button>
              <Button onClick={addItem} disabled={!newItem.component_name} className="bg-[#0D1830] text-white rounded-full hover:opacity-90" data-testid="confirm-add-item">
                <Plus className="w-4 h-4 mr-1" />Add Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pricing adjustment dialog */}
        <Dialog open={!!pricingDialog} onOpenChange={() => setPricingDialog(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-sm">Apply Price Change</DialogTitle>
              <DialogDescription className="text-xs">
                You changed the {pricingDialog?.field} for "{pricingDialog?.componentName}" to {fmtC(pricingDialog?.value)}. 
                This project has {quotes.length} quotes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-3">
              <Button onClick={applyPricingToOne} variant="outline" className="w-full justify-start text-xs h-10 rounded-lg" data-testid="apply-pricing-one">
                <DollarSign className="w-4 h-4 mr-2 text-[#446E87]" />
                Apply to this quote only
              </Button>
              <Button onClick={applyPricingToAll} className="w-full justify-start text-xs h-10 rounded-lg bg-[#0D1830] text-white hover:opacity-90" data-testid="apply-pricing-all">
                <Copy className="w-4 h-4 mr-2" />
                Apply to all {quotes.length} quotes for this project
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Quote list view
  return (
    <div className="space-y-4" data-testid="quote-workflow">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#0D1830] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#0D1830]" />Quotes ({quotes.length})
        </h3>
        <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#0D1830] text-white rounded-full h-8 text-xs px-4 hover:opacity-90" data-testid="create-quote-btn">
          <Plus className="w-3 h-3 mr-1" />New Quote
        </Button>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#1D4053] mx-auto" /></div>
      ) : quotes.length === 0 ? (
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="py-8 text-center">
            <FileText className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
            <p className="text-sm text-[#72A4BF]">No quotes yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {quotes.map((quote) => {
            const sc = QUOTE_STATUS_CONFIG[quote.status] || QUOTE_STATUS_CONFIG.draft;
            const StatusIcon = sc.icon;
            return (
              <Card key={quote.quote_id} className="border-[#E2E8ED] shadow-none hover:border-[#1D4053]/30 transition-colors cursor-pointer" data-testid={`quote-card-${quote.quote_id}`}
                onClick={() => setEditingQuote(quote)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-[#0D1830] truncate">{quote.quote_name}</h4>
                        <Badge className={`${sc.color} border-0 text-[9px] px-1.5 py-0`}><StatusIcon className="w-2.5 h-2.5 mr-0.5" />{sc.label}</Badge>
                        {quote.version > 1 && <Badge className="bg-[#F0F4F7] text-[#446E87] border-0 text-[9px] px-1.5 py-0">v{quote.version}</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#72A4BF]">
                        <span>{quote.customer_name || 'No customer'}</span>
                        <span>{quote.items?.length || 0} items</span>
                        <span className="font-semibold gold-text">{fmtC(quote.total_price)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {quote.status === 'draft' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => sendQuote(quote.quote_id)} disabled={sending}
                            className="h-7 text-[10px] rounded-full px-3 border-[#1D4053] text-[#0D1830] hover:bg-[#72A4BF]/10" data-testid={`send-quote-${quote.quote_id}`}>
                            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}Send
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteQuote(quote.quote_id)} className="h-7 text-[10px] px-2 text-red-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {quote.status === 'sent' && (
                        <Button variant="ghost" size="sm" onClick={() => { setShareLink(`${window.location.origin}/quotes/view/${quote.share_token}`); setShowSend(true); }} className="h-7 text-[10px] px-2">
                          <Link2 className="w-3 h-3" />
                        </Button>
                      )}
                      {['sent', 'viewed'].includes(quote.status) && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => changeQuoteStatus(quote.quote_id, 'accepted')} className="h-7 text-[10px] px-2 text-green-600"><CheckCircle2 className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => changeQuoteStatus(quote.quote_id, 'rejected')} className="h-7 text-[10px] px-2 text-red-500"><XCircle className="w-3 h-3" /></Button>
                        </>
                      )}
                      {quote.status === 'rejected' && (
                        <Button variant="outline" size="sm" onClick={() => changeQuoteStatus(quote.quote_id, 'revised')} className="h-7 text-[10px] rounded-full px-3"><Edit2 className="w-3 h-3 mr-1" />Revise</Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Quote Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create New Quote</DialogTitle><DialogDescription>Start a new quote for this project</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Quote Name *</label><Input value={newQuote.quote_name} onChange={(e) => setNewQuote(p => ({...p, quote_name: e.target.value}))} placeholder="e.g., Initial Bid" data-testid="quote-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Customer Name</label><Input value={newQuote.customer_name} onChange={(e) => setNewQuote(p => ({...p, customer_name: e.target.value}))} /></div>
              <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Customer Email</label><Input type="email" value={newQuote.customer_email} onChange={(e) => setNewQuote(p => ({...p, customer_email: e.target.value}))} /></div>
            </div>
            <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Valid Until</label><Input type="date" value={newQuote.valid_until} onChange={(e) => setNewQuote(p => ({...p, valid_until: e.target.value}))} /></div>
            <div><label className="text-xs font-medium text-[#0D1830] mb-1 block">Terms & Conditions</label><Textarea value={newQuote.terms_conditions} onChange={(e) => setNewQuote(p => ({...p, terms_conditions: e.target.value}))} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
            <Button onClick={createQuote} disabled={!newQuote.quote_name.trim() || creating} className="bg-[#0D1830] text-white rounded-full hover:opacity-90" data-testid="confirm-create-quote">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Create Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" />Quote Sent!</DialogTitle></DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#446E87] mb-4">Share this link:</p>
            <div className="flex items-center gap-2">
              <Input value={shareLink} readOnly className="text-xs bg-[#F7FAFB]" />
              <Button variant="outline" onClick={() => navigator.clipboard.writeText(shareLink)}><Copy className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={() => window.open(shareLink, '_blank')}><ExternalLink className="w-4 h-4" /></Button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setShowSend(false)} className="bg-[#0D1830] text-white rounded-full hover:opacity-90">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
